import { FC, useEffect, useState } from "react"
import BtButton from "../bt-button/bt-button"
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { ServiceWeeklyDigest } from "../../services/service-weekly-digest";
import LanguageSwitcher from "../../controls/LanguageSwitcher/LanguageSwitcher";
import styles from "./page-email-unsubscribe.module.scss";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { Loading } from "../../components/shared";

type Params = {
  p: string,
  s: string,
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  match: RouteTypes['match'];
}

export const PageEmailUnsubscribe: FC<Props> = ({ match }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [missingParams, setMissingParams] = useState(false);
  const { p, s } = match.params;

  useEffect(() => {
    (async() => {
      try {
        const status = await ServiceWeeklyDigest.fetchStatus({ p, s });
        setSubscribed(status !== false);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          if (e.response?.status !== 400) {
            Sentry.captureException(e);
          } else if (e.response.data.error === 'error.missing.parameters') {
            setMissingParams(true);
          }
        }
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  const unsubscribe = async() => {
    setLoading(true);
    try {
      await ServiceWeeklyDigest.unsubscribeEmailNotifications({ p, s });
      setSubscribed(false);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status !== 400) {
        Sentry.captureException(e);
      }
    } finally {
      setLoading(false);
    }
  }

  if (pageLoading) {
    return <Loading type="inline" />
  }

  return (
    <div className={styles.container}>
      <LanguageSwitcher className={styles.languageSwitcher} />
      <div className={styles.content}>
        {missingParams ? (
          <h2>{t('title.missingParams', 'Missing parameters (p, s)')}</h2>
        ) : (
          subscribed ? (
            <>
              <h2>{t('button.subscribed.text', 'Unsubscribe')}</h2>
              <p>{t('description.subscribed', 'We\'re sorry to see you go. Click the button below to unsubscribe from the weekly digest mailing list.')}</p>
              <BtButton loaderShow={loading} onClick={unsubscribe}>{t('button.text', 'Unsubscribe')}</BtButton>
            </>
            ) : (
            <>
              <h2>{t('button.unsubscribed.text', 'You\'ve been unsubscribed')}</h2>
              <p>{t('description.unsubscribed', 'You will no longer receive a weekly digest email from us. If this was a mistake, please login and change your preferences in your account.')}</p>
              <BtButton onClick={() => {
                window.location.href = '/';
              }}>{t('button.returnHome', 'Back to Accurator')}</BtButton>
            </>
          )
        )}
      </div>
    </div>
  )
}